import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import {
  TRAY_ASSEMBLY_HOST_CHROME_EVENT,
  type TrayAssemblyHostChromeDetail,
} from '@peritasai/data-access';
import { periverseTheme } from '@peritasai/ui-shared';
import {
  createFileRoute,
  Outlet,
  redirect,
  useLocation,
} from '@tanstack/react-router';
import { useEffect, useLayoutEffect, useState } from 'react';
import { useAuthStore } from '../../features/auth';
import { PeriverseHeader, PeriverseSidebar } from '../../features/navigation';
import {
  ContentTitleVisibilityContext,
  HostChromeContext,
  PERIVERSE_HEADER_HEIGHT_PX,
  useRouteTitle,
} from '../../shared';
import { contentTitleSx, contentTitleWrapSx } from './-styles';

export const Route = createFileRoute('/_auth')({
  beforeLoad: async ({ location }) => {
    if (location.pathname === '/auth/callback') return;

    await useAuthStore.getState().initialize();

    const { isAuthenticated } = useAuthStore.getState();
    if (!isAuthenticated) throw redirect({ to: '/' });
  },
  component: AuthLayout,
});

function AuthLayout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [hideContentTitle, setHideContentTitle] = useState(false);
  const [hideContentTitleOnDesktop, setHideContentTitleOnDesktop] =
    useState(false);
  const [suppressHostHeader, setSuppressHostHeader] = useState(false);
  const currentPageTitle = useRouteTitle();
  const location = useLocation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const matchesRoute = (routePath: string) =>
    location.pathname === routePath ||
    location.pathname.startsWith(`${routePath}/`);
  const isFindAssetRoute = matchesRoute('/find-asset');
  const isAssembleTrayRoute = matchesRoute('/assemble-tray');
  const isEntityManagementRoute = matchesRoute('/entity-management');
  const isRouteOwnedContentTitleRoute =
    matchesRoute('/assign-to-case') ||
    matchesRoute('/release-from-case') ||
    matchesRoute('/entity-management') ||
    matchesRoute('/case-cart-assembly') ||
    matchesRoute('/case-schedule') ||
    matchesRoute('/case-cart-assembly-detail');

  useEffect(() => {
    const handler = (event: Event) => {
      const { detail } = event as CustomEvent<TrayAssemblyHostChromeDetail>;
      if (detail && typeof detail.active === 'boolean') {
        if (
          (detail.active && !isAssembleTrayRoute) ||
          isEntityManagementRoute
        ) {
          return;
        }
        setSuppressHostHeader(detail.active);
      }
    };
    window.addEventListener(TRAY_ASSEMBLY_HOST_CHROME_EVENT, handler);
    return () => {
      window.removeEventListener(TRAY_ASSEMBLY_HOST_CHROME_EVENT, handler);
    };
  }, [isAssembleTrayRoute, isEntityManagementRoute]);

  const shouldShowContentTitle =
    !hideContentTitle &&
    (!isDesktop || !isFindAssetRoute) &&
    (!isDesktop || !isAssembleTrayRoute) &&
    !isRouteOwnedContentTitleRoute &&
    !hideContentTitleOnDesktop;

  useLayoutEffect(() => {
    setHideContentTitle(false);
    setHideContentTitleOnDesktop(false);
  }, [location.pathname]);

  useLayoutEffect(() => {
    if (!isAssembleTrayRoute && !isEntityManagementRoute) {
      setSuppressHostHeader(false);
    }
  }, [location.pathname, isAssembleTrayRoute, isEntityManagementRoute]);

  useLayoutEffect(() => {
    if (isEntityManagementRoute) {
      if (isDesktop) {
        setSuppressHostHeader(true);
      } else {
        setSuppressHostHeader(false);
      }
    }
  }, [location.pathname, isEntityManagementRoute, isDesktop]);

  const openMobileMenu = () => setMobileMenuOpen(true);
  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <div
      className="flex h-screen overflow-hidden"
      style={{ backgroundColor: periverseTheme.palette.background.default }}
    >
      <PeriverseSidebar
        mobileMenuOpen={mobileMenuOpen}
        onMobileMenuClose={closeMobileMenu}
      />
      <div className="flex-1 flex flex-col min-w-0">
        {!suppressHostHeader && (
          <PeriverseHeader
            onToggleSidebar={openMobileMenu}
            currentPageTitle={currentPageTitle}
          />
        )}
        <main
          className="flex-1 overflow-x-hidden overflow-y-auto content-area"
          style={{
            marginTop: suppressHostHeader ? 0 : PERIVERSE_HEADER_HEIGHT_PX,
          }}
        >
          <div className="w-full ">
            {shouldShowContentTitle && (
              <Box sx={contentTitleWrapSx}>
                <Typography component="h1" sx={contentTitleSx}>
                  {currentPageTitle}
                </Typography>
              </Box>
            )}
            <HostChromeContext.Provider
              value={{
                trayAssemblyFullBleed: suppressHostHeader,
              }}
            >
              <ContentTitleVisibilityContext.Provider
                value={{ setHideContentTitle, setHideContentTitleOnDesktop }}
              >
                <Outlet />
              </ContentTitleVisibilityContext.Provider>
            </HostChromeContext.Provider>
          </div>
        </main>
      </div>
    </div>
  );
}
